import {
  Accordion,
  Button,
  Checkbox,
  Icon,
  SelectMenu,
  Tag,
  TextArea,
  TextField,
  Tooltip,
  Typography,
} from '@scale/scaleui';
import produce from 'immer';
import { useCallback, useMemo, useState } from 'react';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import JSONataEditor from 'react-scale/corp/components/JSONataEditor';
import JSONInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/JSONInputBuilder';
import uuid from 'uuid';
import {
  ZodAny,
  ZodArray,
  ZodBoolean,
  ZodDefault,
  ZodDiscriminatedUnion,
  ZodEffects,
  ZodEnum,
  ZodFirstPartyTypeKind,
  ZodLiteral,
  ZodNativeEnum,
  ZodNumber,
  ZodObject,
  ZodOptional,
  ZodRecord,
  ZodString,
  ZodType,
  ZodUnion,
  z,
} from 'zod';

import AutoCompleteEnumBuilder from './AutoCompleteEnumBuilder';

interface FieldProps<T extends ZodType | ZodDefault<ZodAny>> {
  label: string;
  schema: T;
  value: any;
  onChange: (value: any) => void;
  optional?: boolean;
  fieldIds?: string[];
  startOpen?: boolean;
  isChatEndpointBuilder?: boolean;
  parseErrorMessage?: { id: string; message: string };
  disabled?: boolean;
  codeEditor?: 'jsonata' | 'json' | 'yaml' | 'python' | 'javascript' | 'typescript';
  onErrorChange?: (hasError: boolean) => void;
  isArrayComponent?: boolean;
}

const MaybeTooltip: React.FC<{ text?: string }> = ({ text }) => {
  if (text) {
    return (
      <Tooltip
        target={
          <div>
            <Icon icon="info-circle" size="sm" />
          </div>
        }
        text={<div className="max-w-[360px]">{text}</div>}
      />
    );
  }
  return null;
};

const StringBuilder: React.FC<FieldProps<ZodString>> = ({
  label,
  schema,
  value,
  onChange,
  optional,
  isChatEndpointBuilder,
  disabled,
  codeEditor,
  onErrorChange,
  isArrayComponent = false,
}) => {
  const [error, setError] = useState(false);

  const description = schema._def.description;

  const handleErrorChange = useCallback(
    (hasError: boolean) => {
      setError(hasError);
      onErrorChange?.(hasError);
    },
    [onErrorChange],
  );

  const openJSONataEditor = useCallback(() => {
    if (value) {
      navigator.clipboard.writeText(value);
    }
    window.open('https://try.jsonata.org/', '_blank');
  }, [value]);

  const getEditor = () => {
    const lineCount = (value?.match(/\n/g)?.length ?? 0) + 1;
    const lineHeight = 20;
    const height = Math.min(Math.max(lineCount * lineHeight, 100), 500); // min 100px, max 500px
    switch (codeEditor) {
      case 'jsonata':
        return (
          <div className="w-full">
            {!isArrayComponent && (
              <div className="flex gap-1 items-center">
                <Typography.Subtitle3>
                  {label} {!optional && '*'}
                </Typography.Subtitle3>
                <MaybeTooltip text={description} />
              </div>
            )}
            <div
              style={{
                height: `${height}px`,
                border: error ? '2px solid red' : '2px solid #eeeeee',
              }}
              className="w-full relative"
            >
              <div
                className="absolute top-1 right-1 z-10 bg-white rounded p-1 cursor-pointer"
                onClick={openJSONataEditor}
              >
                <Tooltip
                  target={
                    <div className="opacity-30 hover:opacity-100">
                      <Icon icon="external-link" size="sm" />
                    </div>
                  }
                  text="Copy to clipboard and open JSONata editor"
                />
              </div>
              <JSONataEditor
                value={value}
                onChange={onChange}
                options={{
                  lineNumbers: 'off',
                  scrollBeyondLastLine: false,
                  minimap: { enabled: false },
                  // wordWrap: 'on',
                }}
                onErrorChange={handleErrorChange}
              />
            </div>
          </div>
        );
      default:
        return (
          <div className="w-full">
            {!isArrayComponent && (
              <div className="flex gap-1 items-center">
                <Typography.Subtitle3>
                  {label} {!optional && '*'}
                </Typography.Subtitle3>
                <MaybeTooltip text={description} />
              </div>
            )}

            <TextArea
              className="w-full min-h-[36px]"
              id={uuid.v4()}
              value={value}
              onChange={e => {
                onChange(e.target.value);
              }}
              rows={1}
              dynamic
              required={!optional}
              error={!optional && !value}
              // We used to also disable if label == url but this interferes with the fallback endpoint
              disabled={disabled}
            />
          </div>
        );
    }
  };

  return (
    <div className="w-full">
      {!(isChatEndpointBuilder && label === 'id') && (
        <div className="flex gap-2 items-center">{getEditor()}</div>
      )}
    </div>
  );
};

const AnyBuilder: React.FC<FieldProps<ZodAny>> = ({
  label,
  schema,
  value,
  onChange,
  optional,
  isChatEndpointBuilder,
  disabled,
}) => {
  const description = schema._def.description;

  return (
    <div className="w-full">
      <div className="flex gap-1 items-center">
        <Typography.Subtitle3>
          {label} {!optional && '*'}
        </Typography.Subtitle3>
        <MaybeTooltip text={description} />
      </div>
      <JSONInputBuilder
        name={label}
        existingValue={value || {}}
        updateProperty={onChange}
        disabled={disabled || isChatEndpointBuilder}
      />
    </div>
  );
};

const NumberBuilder: React.FC<FieldProps<ZodNumber>> = ({
  label,
  schema,
  value,
  optional,
  onChange,
  disabled,
}) => {
  const description = schema._def.description;
  return (
    <div className="w-full">
      <TextField
        className="w-full"
        type="number"
        id={uuid.v4()}
        value={value}
        onChange={e => {
          onChange(e.target.value !== '' ? Number(e.target.value) : undefined);
        }}
        label={label}
        required={!optional}
        error={!optional && !value}
        helperText={description}
        disabled={disabled}
      />
    </div>
  );
};

const BooleanBuilder: React.FC<FieldProps<ZodBoolean>> = ({
  label,
  schema,
  value,
  optional,
  onChange,
  disabled,
}) => {
  const description = schema._def.description;
  return (
    <div className="flex gap-2 items-center">
      <div className="flex gap-1 items-center">
        <Typography.Subtitle3>
          {label} {!optional && '*'}
        </Typography.Subtitle3>
      </div>
      <Checkbox
        disabled={disabled}
        id={label}
        checked={value}
        onChange={e => {
          onChange(e.target.checked);
        }}
      />
      <MaybeTooltip text={description} />
    </div>
  );
};

const NativeEnumBuilder: React.FC<FieldProps<ZodNativeEnum<any>>> = ({
  label,
  schema,
  value,
  optional,
  onChange,
  disabled,
}) => {
  const description = schema._def.description;
  const enumOptions: string[] = Object.values(schema._def.values);
  return (
    <>
      <SelectMenu
        options={enumOptions.map((value: string) => ({
          id: value,
          name: value,
        }))}
        selectedOptionId={value}
        setSelected={onChange}
        label={label}
        labelDescription={description}
        required={!optional}
        error={!optional && !value}
        disabled={disabled}
      />
    </>
  );
};

const OptionalBuilder: React.FC<FieldProps<ZodOptional<any>>> = ({
  label,
  schema,
  value,
  onChange,
  fieldIds,
  disabled,
  codeEditor,
  onErrorChange,
}) => {
  const innerSchema = schema.unwrap().describe(schema._def.description);
  return (
    <ZodFormBuilder
      label={label}
      schema={innerSchema}
      value={value}
      onChange={onChange}
      fieldIds={fieldIds}
      optional
      disabled={disabled}
      codeEditor={codeEditor}
      onErrorChange={onErrorChange}
    />
  );
};

const ArrayBuilder: React.FC<FieldProps<ZodArray<any>>> = ({
  label,
  schema,
  value: _value,
  optional,
  onChange,
  fieldIds,
  disabled,
  codeEditor,
  onErrorChange,
}) => {
  const description = schema._def.description;
  const value = useMemo(() => {
    if (_value === undefined || _value === null) {
      return [];
    }
    return _value;
  }, [_value]);
  const innerSchema = schema._def.type;
  const innerTypeName = innerSchema._def.typeName;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setItemErrors] = useState<boolean[]>([]);
  const [dragKey, setDragKey] = useState<number>(0);
  const [updateKey, setUpdateKey] = useState<number>(0);
  const [itemIds, setItemIds] = useState<string[]>(() => (value || []).map(() => uuid.v4()));

  const handleItemErrorChange = useCallback(
    (index: number, hasError: boolean) => {
      setItemErrors(prev => {
        const next = [...prev];
        next[index] = hasError;
        // Propagate up if any item has an error
        onErrorChange?.(next.some(error => error));
        return next;
      });
    },
    [onErrorChange],
  );

  const getDefaultValue = (innerTypeName: ZodFirstPartyTypeKind) => {
    if (innerTypeName === ZodFirstPartyTypeKind.ZodString) return '';
    if (innerTypeName === ZodFirstPartyTypeKind.ZodNumber) return 0;
    if (innerTypeName === ZodFirstPartyTypeKind.ZodBoolean) return false;
    if (innerTypeName === ZodFirstPartyTypeKind.ZodNativeEnum) return '';
    if (innerTypeName === ZodFirstPartyTypeKind.ZodOptional) return undefined;
    if (innerTypeName === ZodFirstPartyTypeKind.ZodObject) return {};
    if (innerTypeName === ZodFirstPartyTypeKind.ZodRecord) return {};
    if (innerTypeName === ZodFirstPartyTypeKind.ZodDiscriminatedUnion) return {};
  };

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }
      const startIndex = result.source.index;
      const endIndex = result.destination.index;
      const newValue = produce(value, (draft: any[]) => {
        const [removed] = draft.splice(startIndex, 1);
        draft.splice(endIndex, 0, removed);
      });
      onChange(newValue);

      setItemIds(prev => {
        const newIds = [...prev];
        const [removedId] = newIds.splice(startIndex, 1);
        newIds.splice(endIndex, 0, removedId);
        return newIds;
      });

      setDragKey(dragKey + 1);
      setUpdateKey(updateKey + 1);
    },
    [onChange, value, dragKey, updateKey],
  );

  const handleDelete = useCallback(
    (index: number) => {
      if (disabled) {
        return;
      }
      const newValueArray = [...value];
      newValueArray.splice(index, 1);

      setItemIds(prev => {
        const newIds = [...prev];
        newIds.splice(index, 1);
        return newIds;
      });

      onChange(newValueArray);
    },
    [disabled, value, onChange],
  );

  const handleAdd = useCallback(() => {
    const newValue = [...value, getDefaultValue(innerTypeName)];
    onChange(newValue);
    setItemIds(prev => [...prev, uuid.v4()]);
  }, [onChange, value, innerTypeName]);

  return (
    <Accordion
      startOpen={false}
      header={
        <div className="flex gap-1 items-center">
          <Typography.Subtitle3>
            {label} {!optional && '*'}
          </Typography.Subtitle3>
          <MaybeTooltip text={description} />
        </div>
      }
      target={
        <div className="flex flex-col gap-1">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`droppable-${label}`}>
              {provided => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {value.map((item: any, index: number) => {
                    const itemId = itemIds[index] || `temp-${index}`;

                    return (
                      <Draggable
                        key={itemId}
                        draggableId={`draggable-${label}-${itemId}`}
                        index={index}
                        isDragDisabled={disabled}
                      >
                        {provided => (
                          <div ref={provided.innerRef} {...provided.draggableProps}>
                            <div className="flex flex-col py-1 mb-2">
                              <div className="flex items-center justify-between rounded-t-md p-1 bg-utility-offWhite">
                                <div {...provided.dragHandleProps} style={{ cursor: 'grab' }}>
                                  <Icon variant="neutral" size="md" icon="grip-vertical" />
                                </div>
                                <div className="text-sm text-neutral-500 text-right flex-1 mr-2">
                                  {label} - {index}
                                </div>
                                <Icon
                                  icon="trash"
                                  tooltip="remove item"
                                  onClick={() => handleDelete(index)}
                                />
                              </div>

                              <div className="flex-grow bg-utility-offWhite rounded-b-md">
                                <ZodFormBuilder
                                  key={`${dragKey}-${index}`}
                                  label={`${label} - ${index}`}
                                  schema={innerSchema}
                                  value={item}
                                  onChange={(newValue: any) => {
                                    const newValueArray = [...value];
                                    newValueArray[index] = newValue;
                                    onChange(newValueArray);
                                  }}
                                  fieldIds={fieldIds}
                                  disabled={disabled}
                                  codeEditor={codeEditor}
                                  onErrorChange={hasError => handleItemErrorChange(index, hasError)}
                                  isArrayComponent={true}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                  <Button className="mt-2" onClick={handleAdd} disabled={disabled}>
                    Add Item
                  </Button>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      }
    />
  );
};

const ObjectBuilder: React.FC<FieldProps<ZodObject<any, any, any>>> = ({
  label,
  schema,
  value,
  optional,
  onChange,
  fieldIds,
  startOpen = false,
  isChatEndpointBuilder = false,
  parseErrorMessage,
  disabled,
  codeEditor,
  onErrorChange,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setFieldErrors] = useState<Record<string, boolean>>({});

  const handleFieldErrorChange = useCallback(
    (key: string, hasError: boolean) => {
      setFieldErrors(prev => {
        const next = { ...prev, [key]: hasError };
        // Propagate up if any field has an error
        onErrorChange?.(Object.values(next).some(error => error));
        return next;
      });
    },
    [onErrorChange],
  );

  const description = schema._def.description;
  const keys = Object.keys(schema._def.shape());
  const canUnset = optional && value !== undefined;
  return (
    <Accordion
      startOpen={startOpen}
      header={
        <div className="flex items-center w-full">
          <Typography.Subtitle3>
            {label} {!optional && '*'}
          </Typography.Subtitle3>
          <MaybeTooltip text={description} />
          {parseErrorMessage?.id === label && (
            <Tooltip
              asChild
              side="top"
              key={parseErrorMessage.id}
              target={
                <div>
                  <Tag className="ml-4" variant="danger">
                    Error
                  </Tag>
                </div>
              }
              text={parseErrorMessage.message}
            />
          )}
          {canUnset && (
            <div
              onClick={e => {
                if (disabled) {
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
                onChange(undefined);
              }}
            >
              <Icon icon="ban" tooltip="unset" />
            </div>
          )}
        </div>
      }
      target={
        <div className="flex flex-col gap-2 w-full">
          {keys.map(key => {
            const subSchema = schema._def.shape()[key];
            return (
              <ZodFormBuilder
                key={key}
                label={key}
                schema={subSchema}
                value={value?.[key]}
                onChange={(newValue: any) => {
                  onChange({
                    ...value,
                    [key]: newValue,
                  });
                }}
                fieldIds={fieldIds}
                startOpen={startOpen}
                isChatEndpointBuilder={isChatEndpointBuilder}
                disabled={disabled}
                codeEditor={codeEditor}
                onErrorChange={hasError => handleFieldErrorChange(key, hasError)}
              />
            );
          })}
        </div>
      }
    />
  );
};

const DiscriminatedUnionBuilder: React.FC<FieldProps<ZodDiscriminatedUnion<any, any>>> = ({
  label,
  schema,
  value,
  optional,
  onChange,
  fieldIds,
  startOpen = false,
  isChatEndpointBuilder = false,
  parseErrorMessage,
  disabled,
  codeEditor,
  onErrorChange,
}) => {
  const description = schema._def.description;
  const discriminatorKey = schema._def.discriminator;
  const discriminatorOptions = [...schema._def.optionsMap.keys()] as [string, ...string[]];
  const selectedDiscriminator = value?.[discriminatorKey] || discriminatorOptions[0];
  const selectedSchema = schema._def.optionsMap.get(selectedDiscriminator)!;
  const providerSubSchema = z.enum(discriminatorOptions);

  const keys = Object.keys(selectedSchema._def.shape()).filter(key => key !== discriminatorKey);
  const canUnset = optional && value !== undefined;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setFieldErrors] = useState<Record<string, boolean>>(() => {
    const keys = [discriminatorKey, ...Object.keys(selectedSchema._def.shape())];
    return Object.fromEntries(keys.map(key => [key, false]));
  });

  const handleFieldErrorChange = useCallback(
    (key: string, hasError: boolean) => {
      setFieldErrors(prev => {
        const next = { ...prev, [key]: hasError };
        // Propagate up if any field has an error
        onErrorChange?.(Object.values(next).some(error => error));
        return next;
      });
    },
    [onErrorChange],
  );

  return (
    <Accordion
      startOpen={startOpen}
      header={
        <div className="flex items-center w-full">
          <Typography.Subtitle3>
            {label} {!optional && '*'}
          </Typography.Subtitle3>
          <MaybeTooltip text={description} />
          {parseErrorMessage?.id === label && (
            <Tooltip
              asChild
              side="top"
              key={parseErrorMessage.id}
              target={
                <div>
                  <Tag className="ml-4" variant="danger">
                    Error
                  </Tag>
                </div>
              }
              text={parseErrorMessage.message}
            />
          )}
          {canUnset && (
            <div
              onClick={e => {
                if (disabled) {
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
                onChange(undefined);
              }}
            >
              <Icon icon="ban" tooltip="unset" />
            </div>
          )}
        </div>
      }
      target={
        <div className="flex flex-col gap-2 w-full">
          <ZodFormBuilder
            key={discriminatorKey}
            label={discriminatorKey}
            schema={providerSubSchema}
            value={selectedDiscriminator}
            onChange={(newValue: any) => {
              if (disabled) {
                return;
              }
              onChange({
                ...value,
                [discriminatorKey]: newValue,
              });
            }}
            fieldIds={fieldIds}
            startOpen={startOpen}
            isChatEndpointBuilder={isChatEndpointBuilder}
            disabled={disabled}
            codeEditor={codeEditor}
            onErrorChange={hasError => handleFieldErrorChange(discriminatorKey, hasError)}
          />
          {keys.map(key => {
            const subSchema = selectedSchema._def.shape()[key];
            return (
              <ZodFormBuilder
                key={key}
                label={key}
                schema={subSchema}
                value={value?.[key]}
                onChange={(newValue: any) => {
                  if (disabled) {
                    return;
                  }
                  onChange({
                    ...value,
                    [key]: newValue,
                  });
                }}
                fieldIds={fieldIds}
                startOpen={startOpen}
                isChatEndpointBuilder={isChatEndpointBuilder}
                disabled={disabled}
                codeEditor={codeEditor}
                onErrorChange={hasError => handleFieldErrorChange(key, hasError)}
              />
            );
          })}
        </div>
      }
    />
  );
};

const UnionBuilder: React.FC<FieldProps<ZodUnion<any>>> = ({
  label,
  schema,
  value,
  optional,
  onChange,
  disabled,
}) => {
  const description = schema._def.description;
  const options = schema._def.options;

  // Check if this is a union of enum and string (pattern: z.union([z.enum([...]), z.string()]))
  const enumOption = useMemo(
    () => options.find((opt: any) => opt._def.typeName === ZodFirstPartyTypeKind.ZodEnum),
    [options],
  );
  const stringOption = useMemo(
    () => options.find((opt: any) => opt._def.typeName === ZodFirstPartyTypeKind.ZodString),
    [options],
  );

  // If this is a union of enum and string, use the AutoCompleteEnumBuilder
  if (enumOption && stringOption) {
    return (
      <AutoCompleteEnumBuilder
        label={label}
        schema={schema}
        value={value}
        optional={optional}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  // For other union types, default to string builder
  return (
    <StringBuilder
      label={label}
      schema={z.string().describe(description || '')}
      value={value}
      onChange={onChange}
      optional={optional}
      disabled={disabled}
    />
  );
};

const EffectsBuilder: React.FC<FieldProps<ZodEffects<any, any, any>>> = ({
  label,
  schema,
  value,
  onChange,
  fieldIds,
  disabled,
  codeEditor,
  onErrorChange,
}) => {
  const innerSchema = schema._def.schema;
  return (
    <ZodFormBuilder
      label={label}
      schema={innerSchema}
      value={value}
      onChange={onChange}
      fieldIds={fieldIds}
      optional
      disabled={disabled}
      codeEditor={codeEditor}
      onErrorChange={onErrorChange}
    />
  );
};

const BuilderMap = {
  [ZodFirstPartyTypeKind.ZodObject]: ObjectBuilder,
  [ZodFirstPartyTypeKind.ZodArray]: ArrayBuilder,
  [ZodFirstPartyTypeKind.ZodOptional]: OptionalBuilder,
  [ZodFirstPartyTypeKind.ZodNativeEnum]: NativeEnumBuilder,
  [ZodFirstPartyTypeKind.ZodString]: StringBuilder,
  [ZodFirstPartyTypeKind.ZodNumber]: NumberBuilder,
  [ZodFirstPartyTypeKind.ZodBoolean]: BooleanBuilder,
  [ZodFirstPartyTypeKind.ZodAny]: AnyBuilder,
  [ZodFirstPartyTypeKind.ZodEnum]: NativeEnumBuilder,
  [ZodFirstPartyTypeKind.ZodDiscriminatedUnion]: DiscriminatedUnionBuilder,
  [ZodFirstPartyTypeKind.ZodRecord]: AnyBuilder,
  [ZodFirstPartyTypeKind.ZodLiteral]: StringBuilder,
  [ZodFirstPartyTypeKind.ZodEffects]: EffectsBuilder,
  [ZodFirstPartyTypeKind.ZodUnion]: UnionBuilder,
};

type ZodFormType =
  | ZodObject<any, any, any>
  | ZodArray<any>
  | ZodOptional<any>
  | ZodNativeEnum<any>
  | ZodString
  | ZodNumber
  | ZodBoolean
  | ZodAny
  | ZodEnum<any>
  | ZodDiscriminatedUnion<any, any>
  | ZodRecord<any>
  | ZodLiteral<any>
  | ZodEffects<any, any, any>
  | ZodUnion<any>;

export const ZodFormBuilder = <T extends ZodFormType | ZodEffects<any, any, any>>(
  props: FieldProps<T | ZodDefault<ZodFormType>>,
) => {
  const getZodSchema = (): ZodFormType | undefined => {
    if (props.schema?._def?.typeName === ZodFirstPartyTypeKind.ZodDefault) {
      return props.schema._def.innerType;
    }
    return props.schema as unknown as ZodFormType;
  };

  const zodSchema = getZodSchema();
  if (!zodSchema) {
    return null;
  }

  const Builder = BuilderMap[zodSchema._def.typeName];
  if (!Builder) {
    throw new Error(`Cannot find builder for type ${zodSchema._def.typeName}`);
  }

  if (props.label === 'fieldId' && props.fieldIds && props.fieldIds.length > 0) {
    const description = zodSchema._def.description;

    return (
      <>
        <div className="flex gap-1 items-center">
          <Typography.Subtitle3>
            {props.label} {!props.optional && '*'}
          </Typography.Subtitle3>
          <MaybeTooltip text={description} />
        </div>
        <SelectMenu
          disabled={props.disabled}
          options={props.fieldIds.map((field: string) => ({
            id: field,
            name: field,
          }))}
          placeholder="Select a field ID"
          selectedOptionId={props.value}
          setSelected={props.onChange}
        />
      </>
    );
  }

  // @ts-ignore
  return <Builder {...props} />;
};
